export const TILE_W = 80;
export const TILE_H = 46;
export const TILE_DEPTH = 52;
export const COLS = 8;
export const ROWS = 8;

export const GROWTH_CHAIN = ['seed', 'sprout', 'sapling', 'young_tree'];
export const MATURE_TREES = ['oak', 'fruit_tree', 'palm'];

// 4 tree species from Plants.png rows 0-3, columns 5-9
export const PLANT_FAMILIES = {
  treeA: ['treeA_1', 'treeA_2', 'treeA_3', 'treeA_4', 'treeA_5'],
  treeB: ['treeB_1', 'treeB_2', 'treeB_3', 'treeB_4', 'treeB_5'],
  treeC: ['treeC_1', 'treeC_2', 'treeC_3', 'treeC_4', 'treeC_5'],
  treeD: ['treeD_1', 'treeD_2', 'treeD_3', 'treeD_4', 'treeD_5'],
};
export const FAMILY_NAMES = Object.keys(PLANT_FAMILIES);

// Build a combined growth map for all families + tree chain
export const GROWTH_MAP = {};
// Tree chain: seed → sprout → sapling → young_tree (young_tree handled specially)
GROWTH_CHAIN.forEach((tileId, index) => {
  if (index < GROWTH_CHAIN.length - 1) {
    GROWTH_MAP[tileId] = GROWTH_CHAIN[index + 1];
  }
});
// Plant families: stage1 → stage2 → stage3 → stage4 (stage4 = mature)
Object.values(PLANT_FAMILIES).forEach((stages) => {
  stages.forEach((tileId, index) => {
    if (index < stages.length - 1) {
      GROWTH_MAP[tileId] = stages[index + 1];
    }
  });
});

// All tile IDs that are still growing (not yet mature)
const ALL_GROWING_TILES = new Set([
  ...GROWTH_CHAIN,
  ...Object.values(PLANT_FAMILIES).flat().slice(0, -1), // all except last stage
  ...Object.values(PLANT_FAMILIES).map((s) => s[s.length - 2]), // second to last
]);
// Actually simpler: anything in GROWTH_MAP keys, plus young_tree and last stages of families
function isGrowingTile(tile) {
  return GROWTH_MAP[tile] !== undefined || tile === 'young_tree';
}

// All seed tile IDs (first stage of each family)
export const ALL_SEEDS = [
  ...FAMILY_NAMES.map((f) => PLANT_FAMILIES[f][0]),
];

export const TILES = [
  { id: 'grass', label: 'Grass', group: 'Terrain' },
  { id: 'dirt', label: 'Dirt', group: 'Terrain' },
  { id: 'stone_path', label: 'Stone', group: 'Terrain' },
  { id: 'sand_raked', label: 'Raked Sand', group: 'Terrain' },
  { id: 'water', label: 'Water', group: 'Terrain' },
  // Legacy growth tiles (still renderable)
  { id: 'seed', label: 'Seed', group: 'Growth' },
  { id: 'sprout', label: 'Sprout', group: 'Growth' },
  { id: 'sapling', label: 'Sapling', group: 'Growth' },
  { id: 'young_tree', label: 'Young Tree', group: 'Growth' },
  { id: 'oak', label: 'Oak', group: 'Trees' },
  { id: 'fruit_tree', label: 'Fruit Tree', group: 'Trees' },
  { id: 'palm', label: 'Palm', group: 'Trees' },
  // Species families
  ...Object.values(PLANT_FAMILIES).flat().map((id) => ({ id, label: id, group: 'Trees' })),
  { id: 'cherry', label: 'Cherry', group: 'Trees' },
  { id: 'autumn_tree', label: 'Autumn', group: 'Trees' },
  { id: 'oak_autumn', label: 'Oak Autumn', group: 'Trees' },
  { id: 'pine', label: 'Pine', group: 'Trees' },
  { id: 'bush', label: 'Bush', group: 'Features' },
  { id: 'rock', label: 'Rock', group: 'Features' },
  { id: 'lantern', label: 'Lantern', group: 'Features' },
  { id: 'pagoda', label: 'Pagoda', group: 'Features' },
  { id: 'pond', label: 'Pond', group: 'Features' },
  { id: 'empty', label: 'Erase', group: 'Other' },
];

export const RANDOM_TILE_POOL = [
  'grass', 'grass', 'grass', 'dirt', 'stone_path', 'sand_raked', 'water',
  'treeA_1', 'treeB_1', 'treeC_1', 'treeD_1',
  'treeA_5', 'treeB_5', 'treeC_5', 'treeD_5',
  'cherry', 'autumn_tree', 'oak', 'oak_autumn', 'pine', 'rock', 'lantern', 'bush', 'pond', 'pagoda', 'empty',
];

export function createEmptyGrid() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill('empty'));
}

export function cloneGrid(grid) {
  return grid.map((row) => [...row]);
}

// Simple seeded PRNG (mulberry32)
function seededRandom(seed) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return hash;
}

export function createInitialGarden(userSeed) {
  const rng = seededRandom(typeof userSeed === 'string' ? hashString(userSeed) : (userSeed || 42));
  const grid = createEmptyGrid();
  // Fill everything with grass — the base world
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      grid[row][col] = 'grass';
    }
  }
  return grid;
}

export function groupTiles() {
  return TILES.reduce((accumulator, tile) => {
    const groupName = tile.group || 'Other';
    if (!accumulator[groupName]) {
      accumulator[groupName] = [];
    }
    accumulator[groupName].push(tile);
    return accumulator;
  }, {});
}

export function advanceGrowth(grid) {
  const nextGrid = cloneGrid(grid);
  let changed = false;

  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      const nextTile = GROWTH_MAP[nextGrid[row][col]];
      if (nextTile) {
        nextGrid[row][col] = nextTile;
        changed = true;
      }
    }
  }

  return { grid: nextGrid, changed };
}

export function clearGarden(grid) {
  const nextGrid = cloneGrid(grid);
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      nextGrid[row][col] = 'empty';
    }
  }
  return nextGrid;
}

export function randomizeGarden(random = Math.random) {
  const nextGrid = createEmptyGrid();
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      const index = Math.floor(random() * RANDOM_TILE_POOL.length);
      nextGrid[row][col] = RANDOM_TILE_POOL[index];
    }
  }
  return nextGrid;
}

export function countTiles(grid) {
  return grid.flat().reduce((accumulator, tileId) => {
    accumulator[tileId] = (accumulator[tileId] || 0) + 1;
    return accumulator;
  }, {});
}

export function findFreeGrassCell(grid, random = Math.random) {
  const free = [];
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      if (grid[row][col] === 'grass') {
        free.push([row, col]);
      }
    }
  }
  if (free.length === 0) return null;
  return free[Math.floor(random() * free.length)];
}

export function getGrowingTile(grid) {
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      const tile = grid[row][col];
      if (isGrowingTile(tile)) {
        return [row, col];
      }
    }
  }
  return null;
}

export function stepGrowth(grid) {
  // Finish growing any tile still in progress
  const growing = getGrowingTile(grid);
  if (growing) {
    const nextGrid = cloneGrid(grid);
    const [row, col] = growing;
    const current = nextGrid[row][col];
    if (current === 'young_tree') {
      nextGrid[row][col] = MATURE_TREES[Math.floor(Math.random() * MATURE_TREES.length)];
    } else {
      nextGrid[row][col] = GROWTH_MAP[current];
    }
    return { grid: nextGrid, changed: true, done: false, changedTile: [row, col] };
  }

  // Plant a fully grown tree directly on a free grass cell
  const freeCell = findFreeGrassCell(grid);
  if (freeCell) {
    const nextGrid = cloneGrid(grid);
    const [row, col] = freeCell;
    const pick = MATURE_TREES[Math.floor(Math.random() * MATURE_TREES.length)];
    nextGrid[row][col] = pick;
    return { grid: nextGrid, changed: true, done: false, changedTile: [row, col] };
  }

  return { grid, changed: false, done: true, changedTile: null };
}

export function getGrowthSummary(grid) {
  const counts = countTiles(grid);
  const allTypes = [
    ...GROWTH_CHAIN,
    ...MATURE_TREES,
    ...Object.values(PLANT_FAMILIES).flat(),
    // Legacy family tiles that may still appear in old grids
    'vine_1','vine_2','vine_3','pepper_1','pepper_2','pepper_3',
    'herb_1','herb_2','herb_3','flower_1','flower_2','flower_3','flower_4',
  ];
  return allTypes.map((tileId) => ({
    tileId,
    count: counts[tileId] || 0,
  }));
}

export function isoPoint(col, row, offX, offY) {
  return {
    x: offX + (col - row) * (TILE_W / 2),
    y: offY + (col + row) * (TILE_H / 2),
  };
}

export function getGridCell(canvasX, canvasY, offX, offY) {
  const relX = canvasX - offX;
  const relY = canvasY - offY;
  return {
    col: Math.round((relX / (TILE_W / 2) + relY / (TILE_H / 2)) / 2),
    row: Math.round((relY / (TILE_H / 2) - relX / (TILE_W / 2)) / 2),
  };
}
